import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Input,
  Label,
  ButtonGroup,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import axios from "axios";
import qrcode from "qrcode-js";
import JsBarcode from "jsbarcode";
import { v4 as uuidv4 } from "uuid";
import { generateEAN13, generateItemReference } from "../../../../utils/ean13Generator";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

function GeneradorEtiquetas() {
  const { user } = useContext(AuthContext);
  
  // URLs de API
  const URL_ARTICULOS = import.meta.env.VITE_URL_ARTICULOS;
  const URL_ARTICULOS_PROPORCION = import.meta.env.VITE_URL_ARTICULOS_PROPORCION;
  const URL_LOGS = import.meta.env.VITE_URL_LOGS;
  const URL_TALLAS = import.meta.env.VITE_URL_TALLAS;

  // Estados principales
  const [tallas, setTallas] = useState([]);
  const [customSizeOrder, setCustomSizeOrder] = useState([]);

  // Estados principales
  const [articulos, setArticulos] = useState([]);
  const [selectedArticulo, setSelectedArticulo] = useState("");
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  
  // Estados para la tabla de proporción
  const [arrayColores, setArrayColores] = useState([]);
  const [arrayTallas, setArrayTallas] = useState([]);
  const [cantidades, setCantidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mostrarTabla, setMostrarTabla] = useState(false);



  // Cargar artículos al montar el componente
  useEffect(() => {
    cargarArticulos();
    axios
      .get(URL_TALLAS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        const allTallas = res.data;
        setTallas(allTallas);

        const customeSizeOrder = allTallas
          .map((t) => t.name)
        setCustomSizeOrder(customeSizeOrder);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // Cargar proporción cuando se selecciona un artículo
  useEffect(() => {
    if (selectedArticulo) {
      seleccionarArticulo();
    } else {
      setMostrarTabla(false);
      setArticuloSeleccionado(null);
    }
  }, [selectedArticulo]);

  const cargarArticulos = async () => {
    try {
      const response = await axios.get(URL_ARTICULOS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      });
      
      const articulosActivos = response.data
        .filter(a => a.is_active === "Si")
        .sort((a, b) => (a.codigo > b.codigo ? 1 : -1));
      
      setArticulos(articulosActivos);
    } catch (error) {
      console.error("Error cargando artículos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los artículos",
      });
    }
  };

  const seleccionarArticulo = async () => {
    if (!selectedArticulo) {
      Swal.fire({
        icon: "warning",
        title: "Selecciona un artículo",
        text: "Por favor selecciona un artículo de la lista",
      });
      return;
    }

    setLoading(true);
    try {
      // Buscar el artículo seleccionado
      const articulo = articulos.find(a => a._id === selectedArticulo);
      setArticuloSeleccionado(articulo);

      // Obtener la proporción del artículo
      const response = await axios.get(
        `${URL_ARTICULOS_PROPORCION}Articulos/${selectedArticulo}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      );

      const allProporcion = response.data.filter(
        (porcion) => porcion.cantidad !== "0"
      );

      if (allProporcion.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Sin proporción",
          text: "Este artículo no tiene proporción definida",
        });
        setLoading(false);
        return;
      }

      // Extraer colores únicos
      let uniqueColors = {};
      let coloresArray = allProporcion
        .map((i) => ({
          name: i.colores[0].name,
          id: i.colores[0]._id,
        }))
        .filter((el) => {
          if (el && !uniqueColors[el.id]) {
            uniqueColors[el.id] = true;
            return true;
          }
          return false;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      setArrayColores(coloresArray);

      // Extraer tallas únicas y ordenarlas
      let uniqueTallas = {};
      let tallasArray = allProporcion
        .map((i) => ({
          name: i.tallas[0].name,
          id: i.tallas[0]._id,
        }))
        .filter((el) => {
          if (el && !uniqueTallas[el.id]) {
            uniqueTallas[el.id] = true;
            return true;
          }
          return false;
        })
        .sort((a, b) => {
          const indexA = customSizeOrder.indexOf(a.name);
          const indexB = customSizeOrder.indexOf(b.name);
          
          if (indexA === -1 && indexB === -1) return a.name.localeCompare(b.name);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          
          return indexA - indexB;
        });

      setArrayTallas(tallasArray);

      // Inicializar cantidades en 0
      const cantidadesIniciales = [];
      coloresArray.forEach(color => {
        tallasArray.forEach(talla => {
          cantidadesIniciales.push({
            id: uuidv4(),
            colorId: color.id,
            colorName: color.name,
            tallaId: talla.id,
            tallaNombre: talla.name,
            cantidad: 0,
          });
        });
      });

      setCantidades(cantidadesIniciales);
      setMostrarTabla(true);
      
    } catch (error) {
      console.error("Error cargando proporción:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar la información del artículo",
      });
    } finally {
      setLoading(false);
    }
  };

   const options = articulos.map((option) => {
    const junta = option.codigo + " " + option.nombre;
    const firstLetter = option.codigo[0].toUpperCase();
    return {
      firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
      ...option,
      junta,
    };
  });

  const handleCantidadChange = (colorId, tallaId, value) => {
    const updatedCantidades = cantidades.map(item => {
      if (item.colorId === colorId && item.tallaId === tallaId) {
        return { ...item, cantidad: parseInt(value) || 0 };
      }
      return item;
    });
    setCantidades(updatedCantidades);
  };

const generarEtiquetasPDF = async (modo = "ean13") => {
  try {
    // Filtrar solo las cantidades mayores a 0
    const cantidadesValidas = cantidades.filter(item => item.cantidad > 0);
    
    if (cantidadesValidas.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Sin cantidades",
        text: "Por favor ingresa al menos una cantidad mayor a 0",
      });
      return;
    }

    setLoading(true);

    // Obtener datos completos de proporción para los códigos EAN
    const response = await axios.get(
      `${URL_ARTICULOS_PROPORCION}Articulos/${selectedArticulo}`,
      {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      }
    );

    const proporcionData = response.data;

    // Etiqueta de tiendas: el codigo de barras debe ser el EAN capturado en la
    // proporcion, que es contra lo que buscan Ventas/Apartados/Traspasos/Ajustes
    // al escanear. Si no hay EAN util ("NA", vacio, con espacios internos o muy
    // largo) se arma como antes: codigo + prefijo color + talla.
    const eanTienda = (p) => {
      const capturado = (p?.EAN || "").trim();
      if (
        capturado &&
        capturado.toUpperCase() !== "NA" &&
        !/\s/.test(capturado) &&
        capturado.length <= 20
      ) {
        return capturado;
      }
      return `${articuloSeleccionado.codigo}${p?.colores[0]?.prefijo || ""}${p?.tallas[0]?.name || ""}`;
    };

    if (modo !== "ean13") {
      // Un EAN repetido en dos color/talla del mismo modelo hace que la caja
      // descuente el renglon equivocado al escanear: avisar antes de imprimir.
      const porCodigo = {};
      proporcionData.forEach((p) => {
        const codigo = eanTienda(p).toUpperCase();
        (porCodigo[codigo] = porCodigo[codigo] || []).push(
          `${p.colores[0]?.name} ${p.tallas[0]?.name}`
        );
      });
      const repetidos = cantidadesValidas
        .map((item) =>
          proporcionData.find(
            (p) => p.colores[0]._id === item.colorId && p.tallas[0]._id === item.tallaId
          )
        )
        .map((p) => p && eanTienda(p).toUpperCase())
        .filter((codigo, i, arr) => codigo && porCodigo[codigo].length > 1 && arr.indexOf(codigo) === i);

      if (repetidos.length > 0) {
        const confirmacion = await Swal.fire({
          icon: "warning",
          title: "Códigos de barras repetidos",
          html:
            "Estos códigos están en más de un color/talla del modelo. Al escanearlos la caja puede descontar la pieza equivocada:<br/><br/>" +
            repetidos.map((codigo) => `<b>${codigo}</b>: ${porCodigo[codigo].join(", ")}`).join("<br/>") +
            "<br/><br/>Corrígelos en la proporción antes de etiquetar.",
          showCancelButton: true,
          confirmButtonText: "Generar de todos modos",
          cancelButtonText: "Cancelar",
        });
        if (!confirmacion.isConfirmed) return;
      }
    }

    // Crear el PDF con formato 46mm x 20mm y alta calidad
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [57, 19],
      compress: false, // Disable compression for better quality
      precision: 16    // Higher precision for better rendering
    });

    let isFirstPage = true;

    // Generar etiquetas según las cantidades especificadas
    for (const item of cantidadesValidas) {
      // Buscar el EAN correspondiente a este color-talla
      const proporcionItem = proporcionData.find(p => 
        p.colores[0]._id === item.colorId && p.tallas[0]._id === item.tallaId
      );

      const colorNombre = proporcionItem?.colores[0]?.name || '';
      const tallaNombre = proporcionItem?.tallas[0]?.name || '';
      const ean = modo === "ean13"
        ? generateEAN13(generateItemReference(articuloSeleccionado.codigo, colorNombre, tallaNombre))
        : eanTienda(proporcionItem);
      const precio1 = proporcionItem?.articulos[0]?.lista1 || 0;
      const precio2 = proporcionItem?.articulos[0]?.lista2 || 0;

      // Generar la cantidad solicitada de etiquetas para esta combinación
      for (let i = 0; i < item.cantidad; i++) {
        if (!isFirstPage) {
          doc.addPage();
        }
        isFirstPage = false;

        // Generar código de barras con alta calidad
        try {
          const barcodeCanvas = document.createElement("canvas");
          JsBarcode(barcodeCanvas, ean, {
            format: modo === "ean13" ? "EAN13" : "CODE128",
            width: 1,
            height: 60,
            displayValue: false,
            fontSize: 6,
            font: "Arial",
            marginTop: 2,
            marginBottom: 2,
            marginLeft: 3,
            marginRight: 3,
            background: "#FFFFFF",
            lineColor: "#000000",
          });
          const barcodeDataUrl = barcodeCanvas.toDataURL("image/png", 1.0);

          // Position barcode at top - full width
          doc.addImage(barcodeDataUrl, "PNG", 1.5, 0.5, 54, 10);
        } catch (barcodeError) {
          console.warn("Error generando código de barras:", barcodeError);
        }

        // Product code with color and size - centered below barcode
        doc.setFontSize(8).setFont(undefined, "normal");
        const productText = ean;
        const textWidth = doc.getTextWidth(productText);
        const centerX = (57 - textWidth) / 2;
        doc.text(productText, centerX, 13.5);

        // Bottom prices - large and prominent
        doc.setFontSize(10).setFont(undefined, "bold");
        doc.text(`$${precio1.toFixed(0)}`, 5, 17.5);
        doc.text(`$${precio2.toFixed(0)}`, 43, 17.5);
      }
    }

    // Registrar en logs
    await axios.post(
      URL_LOGS,
      {
        tipo: "Generar Etiquetas",
        detalle: `${articuloSeleccionado.nombre} ${articuloSeleccionado.codigo} - Total: ${cantidadesValidas.reduce((sum, item) => sum + item.cantidad, 0)} etiquetas`,
        user: user.id,
      },
      {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      }
    );

    // Generate high-quality PDF output
    const pdfOutput = doc.output("blob", { 
      compress: false // Ensure no compression is applied to final output
    });
    const url = URL.createObjectURL(pdfOutput);
    window.open(url, "_blank");
    
    Swal.fire({
      icon: "success",
      title: "¡Etiquetas generadas!",
      text: `Se generaron ${cantidadesValidas.reduce((sum, item) => sum + item.cantidad, 0)} etiquetas`,
      timer: 2000,
    });

    // Limpiar selección y ocultar tabla
    setSelectedArticulo("");
    setArticuloSeleccionado(null);
    setMostrarTabla(false);
    
  } catch (error) {
    console.error("Error generando etiquetas:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudieron generar las etiquetas",
    });
  } finally {
    setLoading(false);
  }
};

  const limpiarCantidades = () => {
    const cantidadesLimpias = cantidades.map(item => ({ ...item, cantidad: 0 }));
    setCantidades(cantidadesLimpias);
  };

  return (
    <>
      <Header />
      <br />
      <br />
      
      {user.menu_articulos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuArticulos"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
           
          </Row>
          
          <h3 align="center">Generador de Etiquetas por Cantidad</h3>
          
          <Row>
            <Col md={4}>
              <Label className="mr-sm-2">Seleccionar Artículo</Label>
              <Autocomplete
                size="small"
                value={selectedArticulo}
                onChange={(event, selectedArticulo) => {
                  setSelectedArticulo(
                    selectedArticulo ? selectedArticulo._id : ""
                  );
                }}
                options={options.sort(
                  (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
                )}
                groupBy={(option) => option.firstLetter}
                getOptionLabel={(option) => option?.junta || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecciona"
                    variant="outlined"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    {option.codigo} {option.nombre}
                  </li>
                )}
              />
            </Col>
          </Row>

          {articuloSeleccionado && (
            <Row>
              <Col md={12}>
                <br />
                <div style={{ 
                  backgroundColor: '#d1ecf1', 
                  border: '1px solid #bee5eb', 
                  borderRadius: '0.25rem',
                  padding: '0.75rem 1.25rem',
                  marginBottom: '1rem'
                }}>
                  <h5>Artículo Seleccionado:</h5>
                  <p><strong>Código:</strong> {articuloSeleccionado.codigo}</p>
                  <p><strong>Nombre:</strong> {articuloSeleccionado.nombre}</p>
                  <p><strong>Piezas por Caja:</strong> {articuloSeleccionado.piezasPorCaja}</p>
                </div>
              </Col>
            </Row>
          )}

          {/* Tabla de cantidades */}
          {mostrarTabla && articuloSeleccionado && (
            <>
              <Row>
                <Col md={8}>
                  <h4>Cantidades por Color y Talla</h4>
                </Col>
                <Col md={4}>
                  <ButtonGroup id="logoutBoton">
                    <Button
                      size="sm"
                      className="btn"
                      color="warning"
                      onClick={limpiarCantidades}
                    >
                      Limpiar <i className="fas fa-eraser"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="success"
                      onClick={() => generarEtiquetasPDF("ean13")}
                      disabled={loading || cantidades.every(item => item.cantidad === 0)}
                    >
                      {loading ? "Generando..." : "Generar PDF CHINA"} <i className="far fa-file-pdf"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="primary"
                      onClick={() => generarEtiquetasPDF("code128")}
                      disabled={loading || cantidades.every(item => item.cantidad === 0)}
                    >
                      {loading ? "Generando..." : "Generar PDF "} <i className="far fa-file-pdf"></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn"
                      color="danger"
                      onClick={() => {
                        setMostrarTabla(false);
                        setSelectedArticulo("");
                        setArticuloSeleccionado(null);
                      }}
                    >
                      Cerrar <i className="fas fa-times"></i>
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>


<div className="bg-gray-50 p-6 rounded-lg">
  <Label className="text-gray-700 font-medium mb-3 block">
    Proporcion de Etiquetas
  </Label>
  
  {/* Contenedor con scroll horizontal */}
  <div 
    style={{ 
      overflowX: 'auto',
      overflowY: 'visible',
      maxWidth: '100%',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      backgroundColor: 'white'
    }}
  >
    <Table 
      size="sm" 
      striped 
      borderless 
      className="mb-0"
      style={{ 
        minWidth: `${Math.max(600, 150 + (arrayTallas.length * 80))}px`,
        width: 'auto'
      }}
    >
      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
        <tr className="border-b border-gray-200">
          <th 
            className="py-3 text-left font-normal sticky-column" 
            style={{ 
              width: "150px",
              minWidth: "150px",
              position: 'sticky',
              left: 0,
              backgroundColor: '#f8f9fa',
              zIndex: 20,
              borderRight: '2px solid #dee2e6'
            }}
          >
            Color / Talla
          </th>
          {arrayTallas.map((talla, index) => (
            <th
              key={talla.id}
              className="py-3 text-center font-medium text-gray-600"
              style={{ 
                width: "80px", 
                minWidth: "80px",
                whiteSpace: 'nowrap'
              }}
            >
              {talla.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {arrayColores.map((color, colorIndex) => (
          <tr key={color.id} className="border-b border-gray-200">
            <td 
              className="py-3 pr-4 text-gray-600 font-medium uppercase sticky-column" 
              style={{ 
                width: "150px",
                minWidth: "150px",
                position: 'sticky',
                left: 0,
                backgroundColor: colorIndex % 2 === 0 ? '#f8f9fa' : 'white',
                zIndex: 10,
                borderRight: '2px solid #dee2e6'
              }}
            >
              {color.name}
            </td>
            {arrayTallas.map((talla) => {
              const item = cantidades.find(
                c => c.colorId === color.id && c.tallaId === talla.id
              );
              return (
                <td 
                  key={talla.id} 
                  className="py-2 text-center" 
                  style={{ 
                    width: "80px", 
                    minWidth: "80px",
                    padding: "8px 4px" 
                  }}
                >
                  <Input
                    type="number"
                    min="0"
                    bsSize="sm"
                    value={item?.cantidad || 0}
                    onChange={(e) =>
                      handleCantidadChange(
                        color.id,
                        talla.id,
                        e.target.value
                      )
                    }
                    style={{ 
                      width: "100%", 
                      textAlign: "center",
                      fontSize: "12px",
                      padding: "4px 8px"
                    }}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>

  {/* Indicador de scroll si hay muchas columnas */}
  {arrayTallas.length > 8 && (
    <div className="text-center mt-2">
      <small className="text-muted">
        <i className="fas fa-arrows-alt-h"></i> Desliza horizontalmente para ver todas las tallas
      </small>
    </div>
  )}
</div>

              <Row>
                <Col md={12}>
                  <br />
                  <div style={{ 
                    backgroundColor: '#d1ecf1', 
                    border: '1px solid #bee5eb', 
                    borderRadius: '0.25rem',
                    padding: '0.75rem 1.25rem',
                    textAlign: 'center'
                  }}>
                    <strong>Total de etiquetas a generar: </strong>
                    <span style={{ 
                      backgroundColor: '#007bff',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      {cantidades.reduce((sum, item) => sum + item.cantidad, 0)}
                    </span>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      ) : (
        <div className="container">
          <div style={{ 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb', 
            borderRadius: '0.25rem',
            padding: '0.75rem 1.25rem',
            textAlign: 'center'
          }}>
            <h4>Acceso Denegado</h4>
            <p>No tienes permisos para acceder a esta sección.</p>
          </div>
        </div>
      )}
      <br />
    </>
  );
}

export default GeneradorEtiquetas;